import JSZip from 'jszip';
import { put } from '@vercel/blob';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';

/**
 * Fetches videos from URLs and creates a zip file
 * @param videos Array of video objects with URLs
 * @returns Promise resolving to a Blob containing the zipped files
 */
export const createZipFromVideos = async (videos: { title: string, url: string }[]): Promise<Blob> => {
  const zip = new JSZip();
  const startTime = Date.now();
  
  try {
    console.log('Creating zip from videos:', videos.map(v => v.title));
    
    // Track successfully processed videos
    let successCount = 0;
    
    // Create a timestamped folder for all videos
    const now = new Date();
    const folderDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const folderName = `videos_${folderDate}`;
    console.log(`Zip: Creating folder '${folderName}' inside zip`);
    
    const videosFolder = zip.folder(folderName);
    if (!videosFolder) {
      throw new Error('Failed to create folder in zip archive');
    }
    
    // Add each video to the zip
    for (let index = 0; index < videos.length; index++) {
      const video = videos[index];
      const videoStartTime = Date.now();
      
      try {
        console.log(`Processing video ${index + 1}/${videos.length}: ${video.title}`);
        
        // Create an XMLHttpRequest to fetch the video with a 30-second timeout
        const blob = await Promise.race<Blob>([
          fetchVideoAsBlob(video.url),
          new Promise<Blob>((_, reject) => setTimeout(() => reject(new Error('Video fetch timed out after 30 seconds')), 30000))
        ]);
        
        // Get the file extension or use default
        const extension = getFileExtension(video.url) || 'mp4';
        
        // Create a valid filename
        const filename = sanitizeFilename(video.title) || `video-${index + 1}`;
        const finalFilename = `${filename}.${extension}`;
        
        console.log(`Zip: Adding file ${index+1}/${videos.length} to folder '${folderName}': ${finalFilename}`);
        
        // Add the video to the folder in the zip with a proper filename
        videosFolder.file(finalFilename, blob);
        successCount++;
        
        const videoDuration = Date.now() - videoStartTime;
        console.log(`Added "${finalFilename}" to zip file (took ${videoDuration}ms)`);
      } catch (error) {
        console.error(`Error processing video ${video.title}:`, error);
      }
    }
    
    // If no videos were successfully processed, throw an error
    if (successCount === 0) {
      throw new Error('Could not process any videos for the zip file');
    }
    
    console.log(`Successfully added ${successCount} of ${videos.length} videos to zip`);
    
    // List all files in the zip for debugging
    const zipContent = Object.keys(zip.files).map(path => ({ 
      path,
      dir: zip.files[path].dir 
    }));
    console.log('Zip contents:', zipContent);
    
    // Generate the zip file with a timeout
    console.log('Generating final zip file...');
    const zipStartTime = Date.now();
    
    const zipBlob = await Promise.race<Blob>([
      zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6 // Medium compression level for balance
        }
      }),
      new Promise<Blob>((_, reject) => setTimeout(() => reject(new Error('Zip generation timed out after 60 seconds')), 60000))
    ]);
    
    const totalDuration = Date.now() - startTime;
    const zipDuration = Date.now() - zipStartTime;
    console.log(`Zip file generated successfully (generation: ${zipDuration}ms, total: ${totalDuration}ms)`);
    console.log(`Zip: Contains ${successCount} videos in folder '${folderName}'`);
    
    return zipBlob;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`Error creating zip file after ${duration}ms:`, error);
    throw new Error('Failed to create zip file');
  }
};

/**
 * Fetches a video blob using XMLHttpRequest which is more reliable for blob URLs
 */
const fetchVideoAsBlob = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    
    xhr.onload = function() {
      if (this.status === 200) {
        resolve(this.response);
      } else {
        reject(new Error(`Failed to fetch video: ${this.statusText}`));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error occurred while fetching video'));
    };
    
    xhr.onabort = function() {
      reject(new Error('Video fetch was aborted'));
    };
    
    xhr.ontimeout = function() {
      reject(new Error('Video fetch timed out'));
    };
    
    // Set a timeout to prevent hanging
    xhr.timeout = 30000; // 30 seconds
    
    // Start the request
    xhr.send();
  });
};

/**
 * Uploads a zip file to Vercel Blob storage
 * @param zipBlob The zip file blob
 * @returns Promise resolving to the permanent URL of the uploaded file
 */
export async function uploadZipToBlob(zipBlob: Blob, filename: string): Promise<string> {
  const startTime = Date.now();
  console.log('Upload: Starting Vercel Blob upload');
  console.log(`Upload: File size: ${(zipBlob.size / (1024 * 1024)).toFixed(2)} MB`);
  
  // Get the token from environment variables
  const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN || '';
  
  if (!token) {
    throw new Error('Vercel Blob token not found in environment variables');
  }
  
  try {
    // Calculate timeout based on file size - at least 60 seconds, plus 10 seconds per MB
    const fileSize = zipBlob.size / (1024 * 1024); // Size in MB
    const timeout = Math.max(60000, 30000 + fileSize * 10000); // Min 60 seconds
    console.log(`Upload: Using timeout of ${Math.round(timeout/1000)} seconds`);
    
    // Add timeout to the upload
    const uploadPromise = put(filename, zipBlob, {
      access: 'public',
      contentType: 'application/zip',
      token
    });

    const timeoutPromise = new Promise<{ url: string }>((_, reject) => 
      setTimeout(() => reject(new Error(`Upload timed out after ${Math.round(timeout/1000)} seconds`)), timeout)
    );

    const { url } = await Promise.race([uploadPromise, timeoutPromise]);
    
    const duration = Date.now() - startTime;
    const uploadSpeed = (zipBlob.size / (1024 * 1024)) / (duration / 1000); // MB/s
    console.log(`Upload: Completed in ${duration}ms (${uploadSpeed.toFixed(2)} MB/s)`);
    return url;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`Upload: Failed after ${duration}ms:`, error);
    throw error;
  }
}

/**
 * Helper function to download the zip file locally (for testing)
 * @param zipBlob The zip file blob
 * @param filename The name to give the downloaded file
 */
export const downloadZipLocally = (zipBlob: Blob, filename: string = 'videos.zip'): void => {
  saveAs(zipBlob, filename);
};

/**
 * Helper function to extract file extension from a URL
 * @param url The URL to extract extension from
 * @returns The file extension or null if not found
 */
const getFileExtension = (url: string): string | null => {
  try {
    // Extract the filename from the URL
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop();
    
    // If no filename found, return null
    if (!filename) return null;
    
    // Extract the extension
    const parts = filename.split('.');
    if (parts.length > 1) {
      return parts.pop()?.toLowerCase() || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting file extension:', error);
    return null;
  }
};

/**
 * Helper function to sanitize a filename
 * @param filename The filename to sanitize
 * @returns A sanitized filename
 */
const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^\w\s.-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/^-+|-+$/g, '')   // Remove leading/trailing hyphens
    .substring(0, 50);         // Limit length
};

/**
 * Checks if a blob URL is valid and accessible
 * @param url The blob URL to check
 * @returns Promise resolving to true if valid, false otherwise
 */
export const isBlobUrlValid = async (url: string): Promise<boolean> => {
  if (!url.startsWith('blob:')) return false;
  
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Error checking blob URL validity:', error);
    return false;
  }
};

/**
 * Creates a zip file directly from video file objects - 
 * A more efficient implementation for direct file access
 * @param videos Array of objects containing title and file
 * @returns Promise resolving to a Blob containing the zipped files
 */
export const createZipFromVideoFiles = async (videos: { title: string, file: File }[]): Promise<Blob> => {
  const zip = new JSZip();
  const startTime = Date.now();
  
  try {
    console.log(`Zip: Starting creation with ${videos.length} video files`);
    
    // List all videos for debugging
    videos.forEach((video, idx) => {
      console.log(`Zip: Video ${idx+1}/${videos.length}: ${video.title} (${(video.file.size / (1024 * 1024)).toFixed(2)} MB, ${video.file.name})`);
    });
    
    // Calculate total size before compression
    const totalSizeBefore = videos.reduce((sum, video) => sum + video.file.size, 0);
    console.log(`Zip: Total size before compression: ${(totalSizeBefore / (1024 * 1024)).toFixed(2)} MB`);
    
    // Track successful files
    let successCount = 0;
    
    // Create a timestamped folder for all videos
    const now = new Date();
    const folderDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const folderName = `videos_${folderDate}`;
    console.log(`Zip: Creating folder '${folderName}' inside zip`);
    
    const videosFolder = zip.folder(folderName);
    if (!videosFolder) {
      throw new Error('Failed to create folder in zip archive');
    }
    
    // Add each video file to the zip
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      const fileStartTime = Date.now();
      
      try {
        // Get file extension from filename or default to mp4
        const extension = video.file.name.split('.').pop() || 'mp4';
        
        // Create a valid filename
        const filename = sanitizeFilename(video.title) || `video-${i + 1}`;
        const finalFilename = `${filename}.${extension}`;
        
        console.log(`Zip: Adding file ${i+1}/${videos.length} to folder '${folderName}': ${finalFilename}`);
        
        // Add the video to the folder in the zip with a proper filename
        videosFolder.file(finalFilename, video.file);
        successCount++;
        
        const fileDuration = Date.now() - fileStartTime;
        console.log(`Zip: Added file ${i + 1}/${videos.length} (${finalFilename}) in ${fileDuration}ms`);
      } catch (error) {
        console.error(`Zip: Error adding file ${video.title} to zip:`, error);
      }
    }
    
    // If no files were successfully added, throw an error
    if (successCount === 0) {
      throw new Error('Could not add any video files to the zip');
    }
    
    console.log(`Zip: ${successCount}/${videos.length} files added, generating final zip file...`);
    
    // List all files in the zip for debugging
    const zipContent = Object.keys(zip.files).map(path => ({ 
      path,
      dir: zip.files[path].dir 
    }));
    console.log('Zip contents:', zipContent);
    
    // Generate the zip file with suitable compression
    const zipStartTime = Date.now();
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6 // Medium compression for balance between size and speed
      }
    });
    
    const zipDuration = Date.now() - zipStartTime;
    const totalDuration = Date.now() - startTime;
    const compressionRatio = (1 - (zipBlob.size / totalSizeBefore)) * 100;
    
    console.log(`Zip: Generated successfully in ${zipDuration}ms (total: ${totalDuration}ms)`);
    console.log(`Zip: Final size: ${(zipBlob.size / (1024 * 1024)).toFixed(2)} MB (${compressionRatio.toFixed(1)}% compression)`);
    console.log(`Zip: Contains ${successCount} videos in folder '${folderName}'`);
    
    return zipBlob;
  } catch (error: any) {
    const duration = Date.now() - startTime;
    console.error(`Zip: Error after ${duration}ms:`, error);
    throw new Error(`Failed to create zip file: ${error?.message || 'Unknown error'}`);
  }
};

/**
 * Uploads individual video files to a folder in Vercel Blob storage.
 * @param videos Array of objects containing title and file
 * @param folderName The name of the folder to create
 * @returns Promise resolving to the URL of the folder
 */
export const uploadVideosToFolder = async (videos: { title: string, file: File }[], folderName: string): Promise<string> => {
  const startTime = Date.now();
  
  // Get the token from environment variables
  const token = process.env.REACT_APP_BLOB_READ_WRITE_TOKEN || '';
  
  if (!token) {
    throw new Error('Vercel Blob token not found in environment variables');
  }
  
  try {
    console.log(`Starting upload of ${videos.length} videos to folder: ${folderName}`);
    
    // Create an array to store all file URLs
    const fileUrls: string[] = [];
    
    // Upload each video file individually
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      const fileStartTime = Date.now();
      
      try {
        // Get file extension from filename or default to mp4
        const extension = video.file.name.split('.').pop() || 'mp4';
        
        // Create a valid filename
        const filename = sanitizeFilename(video.title) || `video-${i + 1}`;
        const finalFilename = `${folderName}/${filename}.${extension}`;
        
        console.log(`Uploading file ${i+1}/${videos.length}: ${finalFilename}`);
        
        // Calculate timeout based on file size - at least 30 seconds, plus 10 seconds per MB
        const fileSize = video.file.size / (1024 * 1024); // Size in MB
        const timeout = Math.max(30000, 10000 + fileSize * 10000); // Min 30 seconds
        
        // Set up the upload with timeout
        const uploadPromise = put(finalFilename, video.file, {
          access: 'public',
          contentType: video.file.type || 'video/mp4',
          token
        });
        
        const timeoutPromise = new Promise<{ url: string }>((_, reject) => 
          setTimeout(() => reject(new Error(`Upload timed out after ${Math.round(timeout/1000)} seconds`)), timeout)
        );
        
        // Execute the upload with timeout
        const { url } = await Promise.race([uploadPromise, timeoutPromise]);
        fileUrls.push(url);
        
        const fileDuration = Date.now() - fileStartTime;
        const uploadSpeed = (video.file.size / (1024 * 1024)) / (fileDuration / 1000); // MB/s
        console.log(`Uploaded file ${i+1}/${videos.length} in ${fileDuration}ms (${uploadSpeed.toFixed(2)} MB/s)`);
        
      } catch (error) {
        console.error(`Error uploading file ${video.title}:`, error);
        // Continue with other files even if one fails
      }
    }
    
    // If no files were successfully uploaded, throw an error
    if (fileUrls.length === 0) {
      throw new Error('Could not upload any video files');
    }
    
    console.log(`Successfully uploaded ${fileUrls.length}/${videos.length} videos`);
    
    // Return the folder URL (the base URL common to all files)
    const firstUrl = fileUrls[0];
    // Extract the base folder URL from the first file URL
    const folderUrl = firstUrl.substring(0, firstUrl.lastIndexOf('/') + 1);
    
    const totalDuration = Date.now() - startTime;
    console.log(`Upload completed in ${totalDuration}ms - Folder URL: ${folderUrl}`);
    
    return folderUrl;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`Upload failed after ${duration}ms:`, error);
    throw error;
  }
};

/**
 * Uploads videos directly to Google Drive using server-generated URLs.
 * Note: This is currently using mock implementation due to OpenSSL issues
 * @param videos Array of objects containing title and file
 * @param folderName The name of the folder to create
 * @returns Promise resolving to the URL of the Google Drive folder
 */
export const uploadVideosToGoogleDrive = async (videos: { title: string, file: File }[], folderName: string): Promise<string | null> => {
  const startTime = Date.now();
  
  // Get API base URL from environment or default to localhost
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  console.log(`Processing ${videos.length} videos for Google Drive folder: ${folderName}`);
  
  try {
    // Step 1: Create a folder in Google Drive
    
    let folderResponse;
    // Import axios dynamically to avoid webpack issues
    const axios = (await import('axios')).default;
    
    // Try to get device ID from multiple sources
    let deviceId = localStorage.getItem('deviceId');
    
    // If not found in localStorage with key 'deviceId', try cookies with 'clipo_device_id'
    if (!deviceId) {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith('clipo_device_id=')) {
          deviceId = cookie.substring('clipo_device_id='.length);
          console.log('Found device ID in cookies:', deviceId);
          
          // Also store in localStorage for future use
          localStorage.setItem('deviceId', deviceId);
          break;
        }
      }
    }
    
    if (!deviceId) {
      console.error('Device ID not found in localStorage or cookies');
      return null;
    }
    
    console.log('Using device ID for Google Drive API:', deviceId);
    
    // Create folder in Google Drive
    folderResponse = await axios.post(`${apiBaseUrl}/google-drive/create-folder`, {
      folderName
    }, {
      headers: {
        'Content-Type': 'application/json',
        'device-id': deviceId
      }
    });
    
    console.log('Folder created successfully:', folderResponse.data);
    
    const { folderId, folderUrl } = folderResponse.data;
    
    if (!folderId || !folderUrl) {
      console.error('Invalid response from folder creation API');
      return null;
    }
    
    // Step 2: Upload each video
    const uploadedFiles = [];
    
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      const fileStartTime = Date.now();
      
      try {
        // Get file extension from filename or default to mp4
        const extension = video.file.name.split('.').pop() || 'mp4';
        
        // Create a valid filename
        const filename = sanitizeFilename(video.title) || `video-${i + 1}`;
        const finalFilename = `${filename}.${extension}`;
        
        console.log(`Processing file ${i+1}/${videos.length}: ${finalFilename}`);
        
        // Step 2a: Get a signed URL for this file
        const signedUrlResponse = await axios.post(`${apiBaseUrl}/google-drive/get-signed-url`, {
          filename: finalFilename,
          mimeType: video.file.type || 'video/mp4',
          size: video.file.size,
          folderId
        }, {
          headers: {
            'Content-Type': 'application/json',
            'device-id': deviceId || '',
            'Origin': window.location.origin
          }
        });
        
        if (!signedUrlResponse.data.uploadUrl) {
          throw new Error('Failed to get signed URL for upload');
        }
        
        const uploadUrl = signedUrlResponse.data.uploadUrl;
        const corsSettings = signedUrlResponse.data.corsSettings || {};
        console.log(`Got signed URL for ${finalFilename}:`, uploadUrl);
        
        // Step 2b: Upload file directly using the signed URL with proper CORS headers
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': video.file.type || 'video/mp4',
            'Content-Length': `${video.file.size}`,
            'Origin': window.location.origin
          },
          body: video.file
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
        }
        
        uploadedFiles.push(finalFilename);
        
        const fileDuration = Date.now() - fileStartTime;
        console.log(`Uploaded file ${i+1}/${videos.length} (${finalFilename}) in ${fileDuration}ms`);
      } catch (error) {
        console.error(`Error uploading file ${video.title}:`, error);
        // Don't use fallback, just record the error and continue
        continue;
      }
    }
    
    // Check if any files were successfully uploaded
    if (uploadedFiles.length === 0) {
      console.warn('No files were successfully uploaded to Google Drive');
      return null;
    } else {
      console.log(`Successfully uploaded ${uploadedFiles.length}/${videos.length} videos to Google Drive folder`);
    }
    
    const totalDuration = Date.now() - startTime;
    console.log(`Upload completed in ${totalDuration}ms - Folder URL: ${folderUrl}`);
    
    // Return the folder URL
    return folderUrl;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`Google Drive upload failed after ${duration}ms:`, error);
    return null;
  }
};
